/**
 * @fileoverview Bundled static data catalog for the report builder data palette —
 *               maps Cube.dev model names to Dexie collection names plus friendly
 *               field metadata (`REPORT_DATA_CATALOG` + lookup helpers).
 * @module local-db/reporting/dataCatalog
 *
 * Responsibilities:
 * - Declare the cubes (InspectionSeries, InspectableEntities, Violations, Contacts)
 *   exposed to report/dashboard builders.
 * - Bind each catalog member to its Dexie collection and stored document field
 *   (`dexieField` must match the real persisted field path on local docs).
 * - Provide lookup helpers (`getCubeById`, `getMeasureByName`, `getDimensionByName`,
 *   `getDexieCollectionForCube`) used by the tenant dashboard data service and the
 *   widget properties panel.
 *
 * Dependencies:
 * - None — static catalog only.
 *
 * Architecture:
 * - Local-first: replaces live `GET /api/data-catalog/cubes` calls for the
 *   frontend; server-side Cube.dev is still used for scheduled/exported report
 *   execution.
 * - `dexieField` paths are read via `getNestedValue`, so nested paths
 *   (e.g. `caseType.name`, `status.name`) are supported.
 *
 * Notes:
 * - Case documents store status in `caseStatus` (canonical `CaseStatus`:
 *   'notStarted' | 'active' | 'closedOut' | 'caseCompleted') — not `status`.
 * - LookupReference fields (caseType, entity status) are objects; bind to the
 *   displayable leaf (`.name`) rather than the object itself.
 */

// ---------------------------------------------------------------------------

export interface CatalogMeasure {
  name: string;
  title: string;
  type: 'count' | 'sum' | 'avg' | 'min' | 'max' | 'countDistinct';
  dexieField: string;
  icon?: string;
  description?: string;
}

export interface CatalogDimension {
  name: string;
  title: string;
  type: 'string' | 'number' | 'date' | 'boolean';
  dexieField: string;
  /** Fallback Dexie paths consulted in order when `dexieField` resolves to
   *  undefined/null — e.g. lookup-reference docs carrying `code` without `name`. */
  dexieFallbacks?: string[];
  icon?: string;
  description?: string;
}

export interface CatalogCube {
  id: string;
  title: string;
  description: string;
  icon: string;
  dexieCollection: 'cases' | 'entities' | 'contacts' | 'violations';
  measures: CatalogMeasure[];
  dimensions: CatalogDimension[];
}

export const REPORT_DATA_CATALOG: CatalogCube[] = [
  {
    id: 'InspectionSeries',
    title: 'Inspections',
    description: 'All inspection cases and their events',
    icon: 'clipboard-check',
    dexieCollection: 'cases',
    measures: [
      { name: 'InspectionSeries.count',         title: 'Total Inspections',    type: 'count',        dexieField: '_id',        icon: 'hash' },
      { name: 'InspectionSeries.openCount',      title: 'Open Inspections',     type: 'count',        dexieField: '_id',        icon: 'folder-open',    description: 'Cases with caseStatus = active or notStarted' },
      { name: 'InspectionSeries.closedCount',    title: 'Closed Inspections',   type: 'count',        dexieField: '_id',        icon: 'folder-check',   description: 'Cases with caseStatus = seriesCompleted, closedOut, or caseCompleted' },
      { name: 'InspectionSeries.eventCount',     title: 'Total Events',         type: 'sum',          dexieField: 'events',     icon: 'calendar' },
      { name: 'InspectionSeries.violationCount', title: 'Total Violations',     type: 'count',        dexieField: '_id',        icon: 'alert-triangle' },
    ],
    dimensions: [
      { name: 'InspectionSeries.status',         title: 'Status',               type: 'string', dexieField: 'caseStatus',               icon: 'activity' },
      { name: 'InspectionSeries.caseType',       title: 'Inspection Type',      type: 'string', dexieField: 'caseType.name',            icon: 'tag',            dexieFallbacks: ['caseType.code'] },
      { name: 'InspectionSeries.entityName',     title: 'Property Name',        type: 'string', dexieField: 'entityName',               icon: 'building' },
      { name: 'InspectionSeries.entityCategory', title: 'Property Category',    type: 'string', dexieField: 'entityCategory',           icon: 'layers' },
      { name: 'InspectionSeries.fiscalYear',     title: 'Fiscal Year',          type: 'number', dexieField: 'fiscalYear',               icon: 'calendar' },
      { name: 'InspectionSeries.accountName',    title: 'Inspection Account',   type: 'string', dexieField: 'inspectionAccountName',    icon: 'briefcase' },
      { name: 'InspectionSeries.createdAt',      title: 'Created Date',         type: 'date',   dexieField: 'createdAt',                icon: 'calendar-plus' },
      { name: 'InspectionSeries.updatedAt',      title: 'Last Updated',         type: 'date',   dexieField: 'updatedAt',                icon: 'clock' },
    ],
  },
  {
    id: 'InspectableEntities',
    title: 'Properties',
    description: 'All inspectable properties (buildings, parcels, sites, complexes, hydrants)',
    icon: 'building',
    dexieCollection: 'entities',
    measures: [
      { name: 'InspectableEntities.count',           title: 'Total Properties',     type: 'count', dexieField: '_id',            icon: 'hash' },
      { name: 'InspectableEntities.activeCount',     title: 'Active Properties',    type: 'count', dexieField: '_id',            icon: 'check-circle' },
      { name: 'InspectableEntities.withOpenCases',   title: 'With Open Cases',      type: 'count', dexieField: '_id',            icon: 'folder-open' },
    ],
    dimensions: [
      { name: 'InspectableEntities.entityCategory',  title: 'Category',             type: 'string', dexieField: 'entityCategory',     icon: 'layers' },
      { name: 'InspectableEntities.name',            title: 'Property Name',        type: 'string', dexieField: 'name',               icon: 'building' },
      { name: 'InspectableEntities.status',          title: 'Status',               type: 'string', dexieField: 'status.name',        icon: 'activity',       dexieFallbacks: ['status.code'] },
      { name: 'InspectableEntities.address',         title: 'Address',              type: 'string', dexieField: 'entityAddress',      icon: 'map-pin',      dexieFallbacks: ['entityLocation.address.fullAddress', 'address.fullAddress'] },
      { name: 'InspectableEntities.addressStreet',   title: 'Street Address',       type: 'string', dexieField: 'address.street',     icon: 'map-pin',      dexieFallbacks: ['entityLocation.address.street'] },
      { name: 'InspectableEntities.addressCity',     title: 'City',                 type: 'string', dexieField: 'address.city',       icon: 'map-pin' },
      { name: 'InspectableEntities.addressState',    title: 'State',                type: 'string', dexieField: 'address.state',      icon: 'map-pin',      dexieFallbacks: ['entityLocation.address.state'] },
      { name: 'InspectableEntities.addressZip',      title: 'Zip Code',             type: 'string', dexieField: 'address.zip',        icon: 'map-pin',      dexieFallbacks: ['entityLocation.address.zip'] },
      { name: 'InspectableEntities.addressDistrict', title: 'District / Zone',      type: 'string', dexieField: 'address.district',   icon: 'map' },
      { name: 'InspectableEntities.occupancyType',   title: 'Occupancy Type',       type: 'string', dexieField: 'buildingDetails.occupancyType', icon: 'home' },
      { name: 'InspectableEntities.constructionType',title: 'Construction Type',    type: 'string', dexieField: 'buildingDetails.constructionType', icon: 'tool' },
      { name: 'InspectableEntities.createdAt',       title: 'Created Date',         type: 'date',   dexieField: 'createdAt',          icon: 'calendar-plus' },
    ],
  },
  {
    id: 'Violations',
    title: 'Violations',
    description: 'All violations captured across inspection events',
    icon: 'alert-triangle',
    dexieCollection: 'violations',
    measures: [
      { name: 'Violations.count',           title: 'Total Violations',     type: 'count', dexieField: '_id',            icon: 'hash' },
      { name: 'Violations.openCount',       title: 'Open Violations',      type: 'count', dexieField: '_id',            icon: 'alert-circle',   description: 'Violations not yet corrected' },
      { name: 'Violations.correctedCount',  title: 'Corrected Violations', type: 'count', dexieField: '_id',            icon: 'check-circle' },
    ],
    dimensions: [
      { name: 'Violations.severity',        title: 'Severity',             type: 'string', dexieField: 'severity',       icon: 'alert-triangle' },
      { name: 'Violations.codeSection',     title: 'Code Section',         type: 'string', dexieField: 'codeSection',    icon: 'book-open' },
      { name: 'Violations.correctionStatus',title: 'Correction Status',    type: 'string', dexieField: 'correctionStatus', icon: 'check-square' },
      { name: 'Violations.caseType',        title: 'Inspection Type',      type: 'string', dexieField: '_caseType',      icon: 'tag' },
      { name: 'Violations.entityId',        title: 'Property',             type: 'string', dexieField: '_entityId',      icon: 'building' },
      { name: 'Violations.eventDate',       title: 'Event Date',           type: 'date',   dexieField: '_eventDate',     icon: 'calendar' },
    ],
  },
  {
    id: 'Contacts',
    title: 'Contacts',
    description: 'All contacts in the department contact pool',
    icon: 'users',
    dexieCollection: 'contacts',
    measures: [
      { name: 'Contacts.count',             title: 'Total Contacts',       type: 'count', dexieField: '_id',            icon: 'hash' },
    ],
    dimensions: [
      { name: 'Contacts.fullName',          title: 'Full Name',            type: 'string', dexieField: 'fullName',       icon: 'user' },
      { name: 'Contacts.role',              title: 'Role',                 type: 'string', dexieField: 'defaultRoles',   icon: 'briefcase' },
      { name: 'Contacts.createdAt',         title: 'Created Date',         type: 'date',   dexieField: 'createdAt',      icon: 'calendar-plus' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------

export function getCubeById(id: string): CatalogCube | undefined {
  return REPORT_DATA_CATALOG.find(c => c.id === id);
}

export function getMeasureByName(cubeName: string, measureName: string): CatalogMeasure | undefined {
  return getCubeById(cubeName)?.measures.find(m => m.name === measureName);
}

export function getDimensionByName(cubeName: string, dimensionName: string): CatalogDimension | undefined {
  return getCubeById(cubeName)?.dimensions.find(d => d.name === dimensionName);
}

export function getDexieCollectionForCube(cubeId: string): CatalogCube['dexieCollection'] | undefined {
  return getCubeById(cubeId)?.dexieCollection;
}
